


















import {
  defineComponent,
  useStore,
  useRoute,
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
} from '@nuxtjs/composition-api'
import loadTestografAsync from '@/helpers/loadTestografAsync'
import { useGetYmClientId } from '@/composition/useGetYmClientId'

export default defineComponent({
  name: 'SurveyButton',
  setup() {
    const store = useStore()
    const route = useRoute()

    const COOKIE_NAME = 'survey_btn_dismissed'

    const getDismissedPaths = (): string[] => {
      const cookie = document.cookie
        .split('; ')
        .find((c) => c.startsWith(`${COOKIE_NAME}=`))
      if (!cookie) return []
      try {
        return JSON.parse(decodeURIComponent(cookie.split('=')[1]))
      } catch {
        return []
      }
    }

    const hasEOsago = ref(false)
    const timeoutId = ref(0)
    const showButton = ref(true)
    const delayPassed = ref(false)
    const scrollPassed = ref(false)
    const DEFAULT_DELAY_SECONDS = 10
    const DEFAULT_DESKTOP_OFFSET_BOTTOM = 40
    const DEFAULT_MOBILE_OFFSET_BOTTOM = 70

    const surveyState = computed(() => store.getters['settings/survey'])
    const scrollPercent = computed<number | null>(
      () => surveyState.value?.scrollPercent ?? null
    )
    const delaySeconds = computed<number>(
      () => surveyState.value?.delaySeconds ?? DEFAULT_DELAY_SECONDS
    )
    const desktopOffsetBottom = computed<number>(
      () =>
        surveyState.value?.desktopOffsetBottom ?? DEFAULT_DESKTOP_OFFSET_BOTTOM
    )
    const mobileOffsetBottom = computed<number>(
      () =>
        surveyState.value?.mobileOffsetBottom ?? DEFAULT_MOBILE_OFFSET_BOTTOM
    )
    const gatePassed = computed(() => delayPassed.value || scrollPassed.value)
    const isMobile = computed<boolean>(
      () => store.getters['mediaQuery/isMobileMq']
    )
    const iframeSrcs = computed(() => store.getters['settings/getIframeSrcs'])

    const mobileStyles = computed(() => ({
      bottom: `${mobileOffsetBottom.value}%`,
    }))

    const desktopStyles = computed(() => ({
      bottom: `${desktopOffsetBottom.value}%`,
    }))

    const dismissButton = () => {
      const path = route.value.path
      const paths = getDismissedPaths()
      if (!paths.includes(path)) paths.push(path)
      document.cookie = `${COOKIE_NAME}=${encodeURIComponent(
        JSON.stringify(paths)
      )}; max-age=${60 * 60 * 24 * 30}; path=/`
      showButton.value = false
    }

    let scrollRafId = 0
    let scrollListenerAttached = false
    let delayTimerId = 0

    function detachScrollListener() {
      if (scrollRafId) {
        window.cancelAnimationFrame(scrollRafId)
        scrollRafId = 0
      }
      if (scrollListenerAttached) {
        window.removeEventListener('scroll', onScroll)
        scrollListenerAttached = false
      }
    }

    function stopDelayTimer() {
      window.removeEventListener('load', startDelayTimer)
      if (delayTimerId) {
        clearTimeout(delayTimerId)
        delayTimerId = 0
      }
    }

    const evaluateScroll = () => {
      const percent = scrollPercent.value
      if (percent == null) return
      const scrollable =
        document.documentElement.scrollHeight - window.innerHeight
      if (scrollable <= 0) return
      if ((window.scrollY / scrollable) * 100 >= percent) {
        scrollPassed.value = true
        stopDelayTimer()
        detachScrollListener()
      }
    }

    function onScroll() {
      if (scrollRafId) return
      scrollRafId = window.requestAnimationFrame(() => {
        scrollRafId = 0
        evaluateScroll()
      })
    }

    const attachScrollListener = () => {
      if (scrollPercent.value == null) return
      if (!scrollListenerAttached) {
        window.addEventListener('scroll', onScroll, { passive: true })
        scrollListenerAttached = true
      }
      onScroll()
    }

    function startDelayTimer() {
      if (delayTimerId) return
      delayTimerId = setTimeout(() => {
        delayTimerId = 0
        delayPassed.value = true
        detachScrollListener()
      }, delaySeconds.value * 1000)
    }

    const initGate = () => {
      if (document.readyState === 'complete') {
        startDelayTimer()
      } else {
        window.addEventListener('load', startDelayTimer, { once: true })
      }
      attachScrollListener()
    }

    onMounted(() => {
      if (getDismissedPaths().includes(route.value.path)) {
        showButton.value = false
      }
      initGate()
    })

    onBeforeUnmount(() => {
      stopDelayTimer()
      detachScrollListener()
    })

    const timeoutHandle = () => {
      showButton.value = true
    }

    watch(iframeSrcs, () => {
      clearTimeout(timeoutId.value)
      if (
        isMobile.value &&
        iframeSrcs.value.find((iframeSrc) => iframeSrc.includes('eOsago'))
      ) {
        hasEOsago.value = true
        showButton.value = false
        timeoutId.value = setTimeout(timeoutHandle, 30000)
      }
    })

    watch(
      () => route.value.path,
      (newPath: string) => {
        delayPassed.value = false
        scrollPassed.value = false
        showButton.value = !getDismissedPaths().includes(newPath)
        stopDelayTimer()
        detachScrollListener()
        initGate()
      }
    )

    const { getYmClientId } = useGetYmClientId()

    const handleClick = async () => {
      if (!(await loadTestografAsync())) return
      const ymClientId = await getYmClientId()
      window.ttgrafSetEmbedParams({
        id: 543476,
        scrollToInvalid: true,
      })
      document.body.classList.add('testograph-open')
      const testografParams = {
        alias: window.location.href,
      }
      if (ymClientId) {
        testografParams.ym_client_id = ymClientId
      }
      window.testograf = {
        params: testografParams,
      }
      store.commit('settings/TOGGLE_SURVEY', true)
    }

    const handleClose = () => {
      store.commit('settings/TOGGLE_SURVEY', false)
      document.body.classList.remove('testograph-open')
    }

    return {
      hasEOsago,
      showButton,
      gatePassed,
      isMobile,
      desktopStyles,
      mobileStyles,
      surveyState,
      dismissButton,
      handleClick,
      handleClose,
    }
  },
})
